import { useMutation } from "@tanstack/react-query";
import { HTTPError } from "ky";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { routingGuidesSchema } from "~/shared/schemas/routing-guide-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

class RoutingGuideNotFoundError extends Error {
	constructor(message = "Routing guide not found") {
		super(message);
		this.name = "RoutingGuideNotFoundError";
	}
}

const getRoutingGuideById = async (id: number) => {
	const url = apiRoutes.GETROUTINGGUIDE.replace(":id", id.toString());
	const response = await getAndValidateTraxAr(url, routingGuidesSchema, {
		onError: (error) => {
			if (error instanceof HTTPError && error.response.status === 404) {
				return {
					success: false,
					error: "Routing guide not found",
					status: 404,
				};
			}
			return null;
		},
	});
	if (!response.success) {
		if (response.status === 404) {
			throw new RoutingGuideNotFoundError();
		}
		throw new Error(
			`Failed to fetch routing guide: ${response.error}`,
			response.details instanceof Error
				? { cause: response.details }
				: undefined,
		);
	}
	return response.data;
};

export const useGetRoutingGuideById = () => {
	const { showError } = useNotificationActions();

	const { mutateAsync, isPending } = useMutation({
		mutationFn: (id: number) => getRoutingGuideById(id),
		onError: (err) => {
			if (err instanceof RoutingGuideNotFoundError) {
				showError(
					"This routing guide has been deleted or modified by another user. " +
						"Please refresh to load the latest version before saving your changes.",
				);
				return;
			}
			showError(`${err}`);
		},
	});

	return {
		fetchRoutingGuideById: mutateAsync,
		isFetchingRoutingGuideById: isPending,
	};
};
